import {
  HitTestInput,
  HitTestResult,
  RectsInput,
  RectsResult,
  ScrollInput,
  INSPECT_OPERATIONS,
  inspectPayload,
  sameGeometry,
} from './protocol.mjs';
import { requireCondition } from './errors.mjs';

// RUN owns transport; DES2 owns CSS transform/viewport/geometry. This binds their API.
export function createInspectFrameBridge({
  transport,
  session,
  geometry,
  transform,
  viewport,
  onTransform,
}) {
  const pending = new Map();
  const listeners = new Set();
  const documents = new Set();
  const transforms = new Set();
  let watched = [];
  let active = true;
  const off = transport.onInvalidate(() => {
    active = false;
    session.invalidate('document_replaced');
    for (const abort of pending.values()) abort.abort();
    pending.clear();
    for (const listener of documents) listener();
  });
  async function hitTest(point, identity) {
    const descriptor = session.requestDescriptor(identity.requestId);
    requireCondition(
      active && descriptor && sameGeometry(descriptor.id, identity),
      'host_request_required',
    );
    const controller = new AbortController();
    pending.set(identity.requestId, controller);
    try {
      const input = inspectPayload(HitTestInput, {
        identity,
        point,
        intent: descriptor.kind,
        seq: descriptor.seq,
      });
      const result = inspectPayload(
        HitTestResult,
        await transport.request(INSPECT_OPERATIONS.hitTest, input, { signal: controller.signal }),
      );
      requireCondition(
        sameGeometry(result.identity, identity) && result.seq === descriptor.seq,
        'inspect_reply_mismatch',
      );
      return result.candidate;
    } finally {
      pending.delete(identity.requestId);
    }
  }
  async function refresh() {
    if (!active || !watched.length) return;
    const identity = geometry();
    const result = inspectPayload(
      RectsResult,
      await transport.request(
        INSPECT_OPERATIONS.rects,
        inspectPayload(RectsInput, { identity, targetIds: watched }),
      ),
    );
    if (!active || !sameGeometry(identity, geometry()) || !sameGeometry(result.identity, identity))
      return;
    for (const target of result.targets)
      if (watched.includes(target.targetId))
        for (const listener of listeners) listener(target.targetId, target.rects, identity);
  }
  return Object.freeze({
    hitTest,
    cancel: (requestId) => pending.get(requestId)?.abort(),
    watch(targetIds) {
      watched = [...new Set(targetIds)];
      void refresh().catch(() => {});
    },
    refresh,
    onRects: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    onDocument: (listener) => {
      documents.add(listener);
      return () => documents.delete(listener);
    },
    scroll(dx, dy) {
      const identity = geometry();
      void transport
        .request(INSPECT_OPERATIONS.scroll, inspectPayload(ScrollInput, { identity, dx, dy }))
        .then(refresh)
        .catch(() => {});
    },
    transform,
    viewport,
    onTransform: onTransform
      ? (listener) => {
          if (!active) return () => {};
          const offTransform = onTransform(() => {
            if (active) listener();
          });
          const release = () => {
            if (!transforms.delete(release)) return;
            offTransform();
          };
          transforms.add(release);
          return release;
        }
      : undefined,
    dispose() {
      off();
      active = false;
      for (const abort of pending.values()) abort.abort();
      pending.clear();
      listeners.clear();
      documents.clear();
      for (const release of transforms) release();
    },
  });
}
